import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { FilterContext } from "../../context/Filter";
import {
  getYearNameList,
  getStateNameList,
  getProductGroupNameList,
  getDealerGroupNameList,
} from "../../pages/SparesSales/SpareSalesKpi";

const SpareSalesFilter = () => {
  const {
    arn: parArn,
    childArn,
    spareSalesPayload,
    setSpareSalesPayload,
    spareSalesFilterPayload,
    setSpareSalesFilterPayload,
  } = useContext(FilterContext);

  const syncInProgress = useRef(false);
  const hasInitialized = useRef(false);

  const [states, setStates] = useState([]);
  const [years, setYears] = useState([]);
  const [dealer, setDealer] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [statuses, setStatuses] = useState([
    { key: "OTC Sales", label: "OTC" },
    { key: "Service Order", label: "S_TIP " },
  ]);

  const [selectedState, setSelectedState] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedDealer, setSelectedDealer] = useState("");
  const [selectedProductGroup, setSelectedProductGroup] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatingDropdown, setUpdatingDropdown] = useState(null);

  const shouldEnableQuery = Boolean(parArn || childArn);

  const cleanPayload = (payload) => {
    const cleaned = { ...payload };
    Object.keys(cleaned).forEach((key) => {
      if (Array.isArray(cleaned[key]) && cleaned[key].length === 0) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  // Sync from context
  useEffect(() => {
    if (spareSalesFilterPayload && !syncInProgress.current) {
      syncInProgress.current = true;
      setSelectedState(spareSalesFilterPayload.state?.[0] || "");
      setSelectedYear(spareSalesFilterPayload.fscl_year?.[0] || "");
      setSelectedDealer(spareSalesFilterPayload.dealws?.[0] || "");
      setSelectedProductGroup(spareSalesFilterPayload.pgws?.[0] || "");
      setSelectedStatus(spareSalesFilterPayload.status || "");
      setTimeout(() => {
        syncInProgress.current = false;
      }, 100);
    }
  }, [spareSalesFilterPayload]);

  // Generate payload
  const generatePayload = useCallback(() => {
    const payload = {
      ...(childArn ? { childArn } : { parArn }),
    };

    if (selectedState) payload.state = [selectedState];
    if (selectedYear) payload.fscl_year = [selectedYear];
    if (selectedDealer) payload.dealws = [selectedDealer];
    if (selectedProductGroup) payload.pgws = [selectedProductGroup];
    if (selectedStatus) payload.status = selectedStatus;

    return cleanPayload(payload);
  }, [
    parArn,
    childArn,
    selectedState,
    selectedYear,
    selectedDealer,
    selectedProductGroup,
    selectedStatus,
  ]);

  // Helper: validate and reset selections
  const validateAndResetSelections = useCallback(
    (results, changedField) => {
      const [stateData, yearData, dealerData, productGroupData] = results;

      if (
        changedField !== "state" &&
        selectedState &&
        stateData &&
        stateData.length > 0 &&
        !stateData.some((i) => (i.key || i.value) === selectedState)
      ) {
        setSelectedState("");
      }

      if (
        changedField !== "fscl_year" &&
        selectedYear &&
        yearData &&
        yearData.length > 0 &&
        !yearData.some((i) => (i.key || i.value) === selectedYear)
      ) {
        setSelectedYear("");
      }

      if (
        changedField !== "dealer" &&
        selectedDealer &&
        dealerData &&
        dealerData.length > 0 &&
        !dealerData.some((i) => (i.key || i.value) === selectedDealer)
      ) {
        setSelectedDealer("");
      }

      if (
        changedField !== "pgws" &&
        selectedProductGroup &&
        productGroupData &&
        productGroupData.length > 0 &&
        !productGroupData.some(
          (i) => (i.key || i.value) === selectedProductGroup
        )
      ) {
        setSelectedProductGroup("");
      }
    },
    [selectedState, selectedYear, selectedDealer, selectedProductGroup]
  );

  // Update all dropdowns
  const updateAllDropdowns = useCallback(
    async (changedField, newValue) => {
      setUpdatingDropdown("all");

      const currentPayload = {
        ...(childArn ? { childArn } : { parArn }),
        search_type: [],
        dropDownValue: [],
      };

      if (changedField !== "reset") {
        if (changedField === "state" && newValue)
          currentPayload.state = [newValue];
        else if (selectedState) currentPayload.state = [selectedState];

        if (changedField === "fscl_year" && newValue)
          currentPayload.fscl_year = [newValue];
        else if (selectedYear) currentPayload.fscl_year = [selectedYear];

        if (changedField === "dealer" && newValue)
          currentPayload.dealws = [newValue];
        else if (selectedDealer) currentPayload.dealws = [selectedDealer];

        if (changedField === "pgws" && newValue)
          currentPayload.pgws = [newValue];
        else if (selectedProductGroup)
          currentPayload.pgws = [selectedProductGroup];

        if (changedField === "status" && newValue)
          currentPayload.status = newValue;
        else if (selectedStatus) currentPayload.status = selectedStatus;
      }

      try {
        const [stateData, yearData, dealerData, productGroupData] =
          await Promise.all([
            getStateNameList(parArn, childArn, currentPayload),
            getYearNameList(parArn, childArn, currentPayload),
            getDealerGroupNameList(parArn, childArn, currentPayload),
            getProductGroupNameList(parArn, childArn, currentPayload),
          ]);

        setStates(stateData || []);
        setYears(yearData || []);
        setDealer(dealerData || []);
        setProductGroups(productGroupData || []);

        validateAndResetSelections(
          [stateData, yearData, dealerData, productGroupData],
          changedField
        );
      } catch (error) {
        console.error(
          `Failed to update all dropdowns after ${changedField} change:`,
          error
        );
      } finally {
        setUpdatingDropdown(null);
      }
    },
    [
      parArn,
      childArn,
      selectedState,
      selectedYear,
      selectedDealer,
      selectedProductGroup,
      selectedStatus,
      validateAndResetSelections,
    ]
  );

  const handleStateChange = useCallback(
    (value) => {
      setSelectedState(value);
      if (value) updateAllDropdowns("state", value);
    },
    [updateAllDropdowns]
  );

  const handleYearChange = useCallback(
    (value) => {
      setSelectedYear(value);
      if (value) updateAllDropdowns("fscl_year", value);
    },
    [updateAllDropdowns]
  );

  const handleDealerChange = useCallback(
    (value) => {
      setSelectedDealer(value);
      if (value) updateAllDropdowns("dealer", value);
    },
    [updateAllDropdowns]
  );

  const handleProductGroupChange = useCallback(
    (value) => {
      setSelectedProductGroup(value);
      if (value) updateAllDropdowns("pgws", value);
    },
    [updateAllDropdowns]
  );

  const handleStatusChange = useCallback(
    (value) => {
      setSelectedStatus(value);
      if (value) updateAllDropdowns("status", value);
    },
    [updateAllDropdowns]
  );

  // Submit
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (isSubmitting || syncInProgress.current || updatingDropdown) return;

      setIsSubmitting(true);
      syncInProgress.current = true;

      const payload = generatePayload();
      setTimeout(() => {
        setSpareSalesFilterPayload(payload);
        setSpareSalesPayload(payload);
        setTimeout(() => {
          setIsSubmitting(false);
          syncInProgress.current = false;
        }, 500);
      }, 0);
    },
    [
      generatePayload,
      isSubmitting,
      updatingDropdown,
      setSpareSalesFilterPayload,
      setSpareSalesPayload,
    ]
  );

  // Reset
  const handleReset = useCallback(() => {
    if (syncInProgress.current || updatingDropdown) return;

    setSelectedState("");
    setSelectedYear("");
    setSelectedDealer("");
    setSelectedProductGroup("");
    setSelectedStatus("");

    const resetPayload = cleanPayload({
      ...(childArn ? { childArn } : { parArn }),
    });

    setSpareSalesFilterPayload(resetPayload);
    setSpareSalesPayload(resetPayload);
    updateAllDropdowns("reset", null);
  }, [
    parArn,
    childArn,
    updatingDropdown,
    setSpareSalesFilterPayload,
    setSpareSalesPayload,
    updateAllDropdowns,
  ]);

  // Initialize dropdowns
  useEffect(() => {
    if (shouldEnableQuery && !hasInitialized.current) {
      hasInitialized.current = true;
      setLoading(true);
      updateAllDropdowns("init", null).finally(() => {
        setLoading(false);
      });
    }
  }, [shouldEnableQuery, updateAllDropdowns]);

  if (loading) {
    return (
      <div className="max-w-full mx-auto p-6 bg-white rounded-md shadow-md flex items-center justify-center">
        <div className="text-gray-500">Loading filters...</div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-full mx-auto p-6 bg-white rounded-md shadow-md flex flex-col gap-2 flex-wrap"
    >
      <div className="flex gap-2">
        <Dropdown
          label="Status"
          value={selectedStatus}
          onChange={handleStatusChange}
          options={statuses}
          loading={false}
        />
        <Dropdown
          label="State"
          value={selectedState}
          onChange={handleStateChange}
          options={states}
          loading={updatingDropdown === "all"}
        />
        <Dropdown
          label="Dealer"
          value={selectedDealer}
          onChange={handleDealerChange}
          options={dealer}
          loading={updatingDropdown === "all"}
        />
        <Dropdown
          label="Year"
          value={selectedYear}
          onChange={handleYearChange}
          options={years}
          loading={updatingDropdown === "all"}
        />
        <Dropdown
          label="Product Group"
          value={selectedProductGroup}
          onChange={handleProductGroupChange}
          options={productGroups}
          loading={updatingDropdown === "all"}
        />
      </div>
      <div className="flex gap-2 justify-center items-center">
        <button
          type="submit"
          disabled={isSubmitting || updatingDropdown}
          className={`px-2 py-1 rounded-md transition ${
            isSubmitting || updatingDropdown
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#2374BF] hover:bg-indigo-700"
          } text-white`}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
        <div className="flex">
          <button
            type="button"
            onClick={handleReset}
            disabled={isSubmitting || updatingDropdown}
            className="bg-gray-400 text-white px-2 py-1 rounded-md hover:bg-gray-700 transition disabled:cursor-not-allowed"
          >
            Reset
          </button>
        </div>
      </div>
    </form>
  );
};

const Dropdown = React.memo(
  ({ label, value, onChange, options, loading = false }) => (
    <div className="flex gap-2 items-center text-xs">
      <label>{label}:</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className={`border border-gray-800 rounded-md py-1 px-2 ${
          loading ? "bg-gray-100 cursor-not-allowed" : ""
        }`}
      >
        <option value="">
          {loading ? `Loading ${label}...` : `Select ${label}`}
        </option>
        {options?.map((item) => (
          <option key={item.key || item.value} value={item.key || item.value}>
            {item.key || item.label}
          </option>
        ))}
      </select>
    </div>
  )
);

Dropdown.displayName = "Dropdown";

export default SpareSalesFilter;
